import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Package, TrendingDown, Building2, MapPin, AlertTriangle, Clock } from 'lucide-react';
import { useAccessibleLocations } from '../hooks/useAccessibleLocations';
import { REPORT_SHIFTS } from '../constants/reportFilters';
import { DateRangeQuickFilter } from '@/components/ui/date-range-quick-filter';
import { useDateRangeDefaults } from '@/hooks/useDateRangeDefaults';
import { reportsService } from '../services/reportsService';

interface ConsumptionFilters {
  locationId: string;
  shift: string;
  dateFrom: string;
  dateTo: string;
}

interface InventoryConsumption {
  id: string;
  insumoName: string;
  locationId: string;
  locationName: string;
  unit: string;
  initialStock: number;
  consumed: number;
  salesConsumption: number;
  otherConsumption: number;
  currentStock: number;
  cost: number;
  totalCost: number;
  status: 'normal' | 'low' | 'critical';
}

export const InventoryConsumptionReport: React.FC = () => {
  const { quickRange, dateFrom, dateTo, setQuickRange, setManualDates } = useDateRangeDefaults('inventoryConsumptionReport');
  const [filters, setFilters] = useState<ConsumptionFilters>({
    locationId: 'all',
    shift: 'all',
    dateFrom: '',
    dateTo: ''
  });
  const [consumptionData, setConsumptionData] = useState<InventoryConsumption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateFilter = (key: keyof ConsumptionFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const { locationsWithAll, defaultLocationId, loading: locationsLoading } = useAccessibleLocations();

  // Auto-seleccionar local cuando se carga
  useEffect(() => {
    if (defaultLocationId && filters.locationId === 'all' && defaultLocationId !== 'all') {
      setFilters(prev => ({ ...prev, locationId: defaultLocationId }));
    }
  }, [defaultLocationId]);

  // Sync date range with filters
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      dateFrom: dateFrom,
      dateTo: dateTo
    }));
  }, [dateFrom, dateTo]);

  const handleDateChange = (key: string, value: string) => {
    if (key === 'dateFrom') {
      setManualDates(value, dateTo);
      updateFilter('dateFrom', value);
    } else if (key === 'dateTo') {
      setManualDates(dateFrom, value);
      updateFilter('dateTo', value);
    }
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await reportsService.getConsumptionReport(filters.locationId, {
        startDate: filters.dateFrom,
        endDate: filters.dateTo,
        shift: filters.shift !== 'all' ? filters.shift : undefined,
      });
      setConsumptionData(data.items || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar el reporte');
      console.error('Error loading consumption report:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    const totalConsumed = consumptionData.reduce((sum, item) => sum + item.consumed, 0);
    const totalCost = consumptionData.reduce((sum, item) => sum + item.totalCost, 0);
    const criticalItems = consumptionData.filter(item => item.status === 'critical').length;
    const lowItems = consumptionData.filter(item => item.status === 'low').length;

    return { totalConsumed, totalCost, criticalItems, lowItems };
  };

  const { totalConsumed, totalCost, criticalItems, lowItems } = calculateTotals();

  const getGroupedConsumptionByInsumo = () => {
    const grouped = consumptionData.reduce<Record<string, {
      insumoName: string;
      unit: string;
      cost: number;
      locations: InventoryConsumption[];
      subtotal: {
        initialStock: number;
        consumed: number;
        salesConsumption: number;
        otherConsumption: number;
        currentStock: number;
        totalCost: number;
        status: 'normal' | 'low' | 'critical';
      };
    }>>((acc, item) => {
      const insumoName = item.insumoName;
      if (!acc[insumoName]) {
        acc[insumoName] = {
          insumoName: insumoName,
          unit: item.unit,
          cost: item.cost,
          locations: [],
          subtotal: {
            initialStock: 0,
            consumed: 0,
            salesConsumption: 0,
            otherConsumption: 0,
            currentStock: 0,
            totalCost: 0,
            status: 'normal' as const
          }
        };
      }
      
      acc[insumoName].locations.push(item);
      acc[insumoName].subtotal.initialStock += item.initialStock;
      acc[insumoName].subtotal.consumed += item.consumed;
      acc[insumoName].subtotal.salesConsumption += item.salesConsumption;
      acc[insumoName].subtotal.otherConsumption += item.otherConsumption;
      acc[insumoName].subtotal.currentStock += item.currentStock;
      acc[insumoName].subtotal.totalCost += item.totalCost;
      
      return acc;
    }, {});

    // Determinar estado del subtotal basado en el estado más crítico
    Object.keys(grouped).forEach(insumoName => {
      const insumo = grouped[insumoName];
      const statuses = insumo.locations.map(loc => loc.status);
      if (statuses.includes('critical')) {
        insumo.subtotal.status = 'critical';
      } else if (statuses.includes('low')) {
        insumo.subtotal.status = 'low';
      } else {
        insumo.subtotal.status = 'normal';
      }
    });

    return Object.values(grouped);
  };

  const groupedConsumption = getGroupedConsumptionByInsumo();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'low': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-green-100 text-green-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'critical': return 'Crítico';
      case 'low': return 'Bajo';
      default: return 'Normal';
    }
  };


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Reporte de Consumo de Insumos</h2>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Local</Label>
              <Select value={filters.locationId} onValueChange={(value) => updateFilter('locationId', value)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {locationsWithAll.map(location => (
                    <SelectItem key={location.id} value={location.id}>
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        {location.nombre}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Rango</Label>
              <DateRangeQuickFilter
                value={quickRange}
                onChange={setQuickRange}
                reportType="inventoryConsumption"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Desde</Label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => updateFilter('dateFrom', e.target.value)}
                className="w-[140px]"
                disabled={quickRange !== 'custom'}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Hasta</Label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => updateFilter('dateTo', e.target.value)}
                className="w-[140px]"
                disabled={quickRange !== 'custom'}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Turno</Label>
              <Select value={filters.shift} onValueChange={(value) => updateFilter('shift', value)}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_SHIFTS.map(shift => (
                    <SelectItem key={shift.id} value={shift.id}>{shift.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleGenerateReport}
              disabled={loading}
              className="bg-macartur-primary hover:bg-macartur-primary/90"
            >
              {loading ? 'Cargando...' : 'Generar Reporte'}
            </Button>
          </div>
        </CardContent>
      </Card>



      {/* Detalle de Consumo por Sucursal e Insumo - Ancho completo */}
      <Card>
        <CardHeader>
          <CardTitle>Detalle de Consumo por Sucursal e Insumo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Insumo</TableHead>
                  <TableHead>Sucursal</TableHead>
                  <TableHead className="text-right">Stock Inicial</TableHead>
                  <TableHead className="text-right">Consumido</TableHead>
                  <TableHead className="text-right">Por Ventas</TableHead>
                  <TableHead className="text-right">Otros</TableHead>
                  <TableHead className="text-right">Stock Actual</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupedConsumption.map((insumoGroup) => (
                  <>
                    {/* Header del grupo de insumo */}
                    <TableRow key={`header-${insumoGroup.insumoName}`} className="bg-gray-50 border-b-2">
                      <TableCell colSpan={7} className="font-bold text-gray-800">
                        <div className="flex items-center">
                          <Package className="w-5 h-5 mr-2 text-gray-600" />
                          {insumoGroup.insumoName} ({insumoGroup.unit})
                        </div>
                      </TableCell>
                    </TableRow>
                    
                    {/* Filas de sucursales */}
                    {insumoGroup.locations.map((location) => (
                      <TableRow key={`${insumoGroup.insumoName}-${location.locationId}`} className="bg-white">
                        <TableCell className="pl-8">
                          <span className="text-gray-500 text-sm">└</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                            <span className="text-sm">{location.locationName}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-medium">{location.initialStock}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-bold text-red-600">{location.consumed}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-green-600">{location.salesConsumption}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-orange-600">{location.otherConsumption}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-medium">{location.currentStock}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {/* Fila de subtotal por insumo */}
                    <TableRow key={`subtotal-${insumoGroup.insumoName}`} className="bg-blue-50 border-t border-blue-200">
                      <TableCell className="pl-4 font-semibold text-blue-800">
                        Subtotal {insumoGroup.insumoName}
                      </TableCell>
                      <TableCell className="font-semibold text-blue-800">
                        {insumoGroup.locations.length} sucursales
                      </TableCell>
                      <TableCell className="text-right font-bold text-blue-900">
                        {insumoGroup.subtotal.initialStock}
                      </TableCell>
                      <TableCell className="text-right font-bold text-blue-900">
                        {insumoGroup.subtotal.consumed}
                      </TableCell>
                      <TableCell className="text-right font-bold text-blue-900">
                        {insumoGroup.subtotal.salesConsumption}
                      </TableCell>
                      <TableCell className="text-right font-bold text-blue-900">
                        {insumoGroup.subtotal.otherConsumption}
                      </TableCell>
                      <TableCell className="text-right font-bold text-blue-900">
                        {insumoGroup.subtotal.currentStock}
                      </TableCell>
                    </TableRow>
                  </>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

    </div>
  );
};